import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { projectApi } from '../services/projectApi';

// doc_type badge colors
const DOC_TYPE_COLORS = {
  prd: { bg: '#EFF6FF', text: '#2563EB', label: 'PRD' },
  techspec: { bg: '#F0FDF4', text: '#16A34A', label: 'TECHSPEC' },
  plan: { bg: '#FFF7ED', text: '#EA580C', label: 'PLAN' },
  ref: { bg: '#F5F3FF', text: '#7C3AED', label: 'REF' },
  guide: { bg: '#ECFDF5', text: '#059669', label: 'GUIDE' },
  workflow: { bg: '#FDF2F8', text: '#DB2777', label: 'WORKFLOW' },
  issue: { bg: '#FEF2F2', text: '#DC2626', label: 'ISSUE' },
  postmortem: { bg: '#FEF9C3', text: '#CA8A04', label: 'POSTMORTEM' },
  other: { bg: '#F1F5F9', text: '#64748B', label: 'OTHER' },
};

// Generate slug ID from heading text
function slugify(text) {
  return text
    .toLowerCase()
    .replace(/[^\w\s가-힣-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
}

// Extract headings for TOC
function extractHeadings(text) {
  if (!text) return [];
  const headings = [];
  const lines = text.split('\n');
  let inCodeBlock = false;
  for (const line of lines) {
    if (line.trim().startsWith('```')) {
      inCodeBlock = !inCodeBlock;
      continue;
    }
    if (inCodeBlock) continue;
    const match = line.match(/^(#{2,4})\s+(.+)$/);
    if (match) {
      const raw = match[2].trim();
      headings.push({
        level: match[1].length,
        text: raw,
        id: slugify(raw),
      });
    }
  }
  return headings;
}

// Format date to KST
function formatDate(dateStr) {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

// Custom renderers for ReactMarkdown
const markdownComponents = {
  // Headings with IDs for TOC linking
  h1: ({ children }) => <h1 id={slugify(String(children))}>{children}</h1>,
  h2: ({ children }) => <h2 id={slugify(String(children))}>{children}</h2>,
  h3: ({ children }) => <h3 id={slugify(String(children))}>{children}</h3>,
  h4: ({ children }) => <h4 id={slugify(String(children))}>{children}</h4>,
  h5: ({ children }) => <h5 id={slugify(String(children))}>{children}</h5>,
  h6: ({ children }) => <h6 id={slugify(String(children))}>{children}</h6>,

  // Code blocks with syntax highlighting
  code: ({ inline, className, children, ...props }) => {
    const match = /language-(\w+)/.exec(className || '');
    const lang = match ? match[1] : '';

    if (!inline && (lang || String(children).includes('\n'))) {
      return (
        <SyntaxHighlighter
          style={oneLight}
          language={lang || 'text'}
          PreTag="div"
          customStyle={{
            margin: '12px 0',
            borderRadius: '8px',
            fontSize: '13px',
            border: '1px solid #E8E8E8',
          }}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      );
    }
    return <code className="pdv-inline-code" {...props}>{children}</code>;
  },

  // Links open in new tab
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="pdv-link">
      {children}
    </a>
  ),

  // Tables
  table: ({ children }) => (
    <div className="pdv-table-wrap">
      <table>{children}</table>
    </div>
  ),

  // Checklist items
  li: ({ children, className, ...props }) => {
    const isChecked = props.checked;
    if (typeof isChecked === 'boolean') {
      return (
        <li className="pdv-checklist-item">
          <input type="checkbox" checked={isChecked} readOnly />
          <span>{children}</span>
        </li>
      );
    }
    return <li {...props}>{children}</li>;
  },

  // Images
  img: ({ src, alt }) => (
    <img src={src} alt={alt || ''} className="pdv-img" loading="lazy" />
  ),

  // Blockquote
  blockquote: ({ children }) => (
    <blockquote className="pdv-blockquote">{children}</blockquote>
  ),
};

const ProjectDocViewer = ({ project, docId, onBack }) => {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showToc, setShowToc] = useState(true);

  useEffect(() => {
    if (!project || !docId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    projectApi.getDoc(project, docId)
      .then(res => {
        if (!cancelled) {
          setDoc(res.data || res);
          setLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [project, docId]);

  const headings = useMemo(() => extractHeadings(doc?.content), [doc?.content]);

  const docType = doc?.doc_type || 'other';
  const badge = DOC_TYPE_COLORS[docType] || DOC_TYPE_COLORS.other;

  const STATUS_LABELS = {
    ok: '동기화 완료',
    changed: '변경사항 있음',
    review: '코드 검수 필요',
    wip: '진행중',
    unknown: '확인 필요',
  };
  const STATUS_COLORS = {
    ok: '#22C55E', changed: '#EAB308', review: '#3B82F6', wip: '#EF4444', unknown: '#1a1a1a',
  };
  const docStatus = doc?.doc_status || 'ok';

  const contentRef = useRef(null);

  // TOC width resize
  const [tocWidth, setTocWidth] = useState(() => {
    const saved = localStorage.getItem('mb_tocWidth');
    return saved ? parseInt(saved, 10) : 200;
  });
  useEffect(() => { localStorage.setItem('mb_tocWidth', String(tocWidth)); }, [tocWidth]);

  const handleTocResizeStart = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = tocWidth;
    const onMove = (ev) => {
      const diff = ev.clientX - startX;
      setTocWidth(Math.max(120, Math.min(500, startW + diff)));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [tocWidth]);

  const handleTocClick = (id) => {
    const el = document.getElementById(id);
    if (el && contentRef.current) {
      const container = contentRef.current;
      const top = el.offsetTop - container.offsetTop;
      container.scrollTo({ top, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-400 text-sm">문서를 불러오는 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3">
        <div className="text-red-400 text-sm">오류: {error}</div>
        <button onClick={onBack} className="text-sm text-blue-500 hover:underline">돌아가기</button>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3">
        <div className="text-gray-400 text-sm">문서를 찾을 수 없습니다</div>
        <button onClick={onBack} className="text-sm text-blue-500 hover:underline">돌아가기</button>
      </div>
    );
  }

  return (
    <div className="pdv-container">
      {/* Header */}
      <div className="pdv-header">
        <div className="pdv-header-top">
          <button onClick={onBack} className="pdv-back-btn" title="돌아가기">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            <span>노트 목록</span>
          </button>
          <div className="pdv-header-meta">
            <span className="pdv-project-name">{project}</span>
            <span className="pdv-badge" style={{ background: badge.bg, color: badge.text }}>
              {badge.label}
            </span>
          </div>
        </div>
        <h1 className="pdv-title">
          <span>{doc.title || doc.file_name || '제목 없음'}</span>
          <span className="pdv-title-status-label" style={{ color: STATUS_COLORS[docStatus] }}>
            {STATUS_LABELS[docStatus]}
            <span className="pdv-title-status-dot" style={{ background: STATUS_COLORS[docStatus] }} />
          </span>
        </h1>
        <div className="pdv-meta-row">
          {doc.file_path && <span className="pdv-meta-item" title="파일 경로">{doc.file_path}</span>}
          {doc.synced_at && <span className="pdv-meta-item" title="마지막 동기화">동기화: {formatDate(doc.synced_at)}</span>}
          {doc.updated_at && <span className="pdv-meta-item" title="수정일">수정: {formatDate(doc.updated_at)}</span>}
        </div>
      </div>

      {/* Content area with optional TOC */}
      <div className="pdv-body">
        {/* TOC sidebar */}
        {headings.length > 0 && (
          <>
            <div className={`pdv-toc ${showToc ? '' : 'pdv-toc-collapsed'}`} style={showToc ? { width: `${tocWidth}px` } : undefined}>
              <div className="pdv-toc-header" onClick={() => setShowToc(!showToc)}>
                <span className="pdv-toc-toggle">{showToc ? '▼' : '▶'}</span>
                <span>목차</span>
              </div>
              {showToc && (
                <nav className="pdv-toc-list">
                  {headings.map((h, i) => (
                    <button
                      key={i}
                      className="pdv-toc-item"
                      style={{ paddingLeft: `${(h.level - 2) * 12 + 8}px` }}
                      onClick={() => handleTocClick(h.id)}
                    >
                      {h.text}
                    </button>
                  ))}
                </nav>
              )}
            </div>
            {showToc && <div className="pdv-toc-resize" onMouseDown={handleTocResizeStart} />}
          </>
        )}

        {/* Rendered markdown */}
        <div ref={contentRef} className="pdv-content">
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkBreaks]}
            components={markdownComponents}
          >
            {doc.content || ''}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
};

export default ProjectDocViewer;
